import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API_STATUS, hasUserRegisteredToSession } from 'store/constants';
import {
	fetchGuestDetails,
	addGuestUser,
	registerGuestUserToSession,
	guestAlreadyRegisteredToSession,
	updateGuestUser,
	addGuestUserSessionFeedback
} from './guestAPI';

const initialState = {
	info: {},
	event: {}
};

// thunk & service
export const getGuestInfoAsync = createAsyncThunk('guest/fetchGuestUser', async guestInfo => {
	const { email, phoneNumber } = guestInfo;
	const guestData = await fetchGuestDetails(email, phoneNumber);
	// The value we return becomes the `fulfilled` action payload
	return guestData;
});

export const addGuestUserAsync = createAsyncThunk('guest/addGuestUser', async guest => {
	const guestData = await addGuestUser({ ...guest, email: guest.email.toLowerCase() });
	// The value we return becomes the `fulfilled` action payload
	return guestData;
});

export const updateGuestUserAsync = createAsyncThunk('guest/updateGuestUser', async guestInfo => {
	const { guestId, ...guest } = guestInfo;
	await updateGuestUser(guestId, { ...guest, email: guest.email.toLowerCase() });
	// The value we return becomes the `fulfilled` action payload
	return guestInfo;
});

export const checkIfGuestAlreadyRegisteredToSessionAsync = createAsyncThunk(
	'guest/guestAlreadyRegisteredToSession',
	async guestSessionData => {
		const { guestId, eventId } = guestSessionData;
		const guestSessionDetails = await guestAlreadyRegisteredToSession(guestId, eventId);
		// The value we return becomes the `fulfilled` action payload
		const alreadyRegistered = guestSessionDetails?.eventId === eventId;
		return { alreadyRegistered, ...guestSessionDetails };
	}
);

export const registerGuestUserToSessionAsync = createAsyncThunk(
	'guest/registerGuestUserToSession',
	async registrationData => {
		const { guestId, eventId, data } = registrationData;
		const guestData = await registerGuestUserToSession(guestId, eventId, data);
		// The value we return becomes the `fulfilled` action payload
		return guestData;
	}
);

export const unregisterGuestUserToSessionAsync = createAsyncThunk(
	'guest/unregisterGuestUserToSession',
	async registrationData => {
		const { guestId, eventId } = registrationData;
		const guestData = await registerGuestUserToSession(guestId, eventId);
		// The value we return becomes the `fulfilled` action payload
		return guestData;
	}
);

export const addGuestUserSessionFeedbackAsync = createAsyncThunk(
	'guest/addGuestUserSessionFeedback',
	async registrationData => {
		const { guestId, eventId, feedback } = registrationData;
		const guestSessionFeedback = await addGuestUserSessionFeedback(guestId, eventId, feedback);
		// The value we return becomes the `fulfilled` action payload
		return guestSessionFeedback;
	}
);

export const guestSlice = createSlice({
	name: 'guest',
	initialState,
	reducers: {
		setGuest: (state, action) => {
			// TODO: setter
		}
	},

	extraReducers: builder => {
		builder
			.addCase(getGuestInfoAsync.pending, state => {
				state.infoStatus = API_STATUS.PENDING;
			})
			.addCase(getGuestInfoAsync.rejected, state => {
				state.infoStatus = API_STATUS.REJECTED;
			})
			.addCase(getGuestInfoAsync.fulfilled, (state, action) => {
				state.info = action.payload;
				state.infoStatus = API_STATUS.COMPLETED;
			})
			.addCase(addGuestUserAsync.pending, state => {
				state.infoStatus = API_STATUS.PENDING;
			})
			.addCase(addGuestUserAsync.rejected, state => {
				state.infoStatus = API_STATUS.REJECTED;
			})
			.addCase(addGuestUserAsync.fulfilled, (state, action) => {
				state.info = action.payload;
				state.infoStatus = API_STATUS.COMPLETED;
			})
			.addCase(updateGuestUserAsync.pending, state => {
				state.infoStatus = API_STATUS.PENDING;
			})
			.addCase(updateGuestUserAsync.rejected, state => {
				state.infoStatus = API_STATUS.REJECTED;
			})
			.addCase(updateGuestUserAsync.fulfilled, (state, action) => {
				state.info = action.payload;
				state.infoStatus = API_STATUS.COMPLETED;
			})
			.addCase(registerGuestUserToSessionAsync.pending, state => {
				state.eventRegisterStatus = API_STATUS.PENDING;
			})
			.addCase(registerGuestUserToSessionAsync.rejected, state => {
				state.eventRegisterStatus = API_STATUS.REJECTED;
			})
			.addCase(registerGuestUserToSessionAsync.fulfilled, (state, action) => {
				state.event = action.payload;
				state.eventRegisterStatus = API_STATUS.COMPLETED;
			})
			.addCase(checkIfGuestAlreadyRegisteredToSessionAsync.rejected, state => {
				state.eventFeedbackStatus = API_STATUS.REJECTED;
			})
			.addCase(checkIfGuestAlreadyRegisteredToSessionAsync.fulfilled, (state, action) => {
				state.event = action.payload;
				state.eventFeedbackStatus = API_STATUS.COMPLETED;
			})
			.addCase(addGuestUserSessionFeedbackAsync.pending, state => {
				state.eventFeedbackStatus = API_STATUS.PENDING;
			})
			.addCase(addGuestUserSessionFeedbackAsync.rejected, state => {
				state.eventFeedbackStatus = API_STATUS.REJECTED;
			})
			.addCase(addGuestUserSessionFeedbackAsync.fulfilled, (state, action) => {
				state.event.feedback = action.payload;
				state.eventFeedbackStatus = API_STATUS.COMPLETED;
			});
	}
});

export const selectGuest = state => state.guest.info;
export const selectGuestId = state => state.guest.info?.gid;
export const isGuestRegistrationToSession = state => hasUserRegisteredToSession(state.guest.event?.status);
export const isGuestSessionFeedbackComplete = state => !!state.guest.event?.feedback;

export const { setGuest } = guestSlice.actions;

export default guestSlice.reducer;
